/**
 * AboutOperatorPage - Pagina publică "Despre Operator"
 * Identifică operatorul GDPR conform cerințelor ANSPDCP + Netopia.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Building2, Mail, Phone, MapPin, ExternalLink, Shield, FileText, RotateCcw, Sparkles, X } from "lucide-react";
import { ETESA_OPERATOR as OP } from "@/config/operator";
import LegalFooter from "@/components/LegalFooter";

export default function AboutOperatorPage() {
  const navigate = useNavigate();
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    document.title = `Despre Operator | ${OP.brand}`;
    const m = document.querySelector('meta[name="description"]');
    if (m) m.setAttribute("content", `Date oficiale ${OP.legal_name} — Operator platforma ${OP.brand}. CUI ${OP.cui}, ${OP.reg_com}.`);
    const c = document.querySelector('link[rel="canonical"]');
    if (c) c.setAttribute("href", `${OP.website}/despre`);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex flex-col">
      <div className="flex-1 max-w-4xl mx-auto px-4 py-8 w-full">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6 text-slate-600" data-testid="about-back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Înapoi
        </Button>

        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden" data-testid="about-page-content">
          <div className="p-6 border-b border-slate-100 bg-gradient-to-r from-teal-50 to-blue-50">
            <div className="flex items-center gap-3">
              <Building2 className="w-8 h-8 text-teal-600" />
              <div>
                <h1 className="text-2xl font-bold text-slate-800">Despre Operator</h1>
                <p className="text-sm text-slate-500 mt-0.5">
                  Identificarea operatorului platformei {OP.brand}
                </p>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-6">
            <section>
              <h2 className="text-lg font-bold text-slate-800 mb-3">Date juridice</h2>
              <div className="bg-slate-50 rounded-lg p-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <Row label="Denumire" value={OP.legal_name} />
                <Row label="CUI" value={OP.cui} />
                <Row label="Nr. Reg. Comerțului" value={OP.reg_com} />
                <Row label="EUID" value={OP.euid} />
                <Row label="Data înființării" value={OP.founded_at} />
                <Row label="Tip platformă" value={`SaaS · ${OP.brand}`} />
              </div>
            </section>

            <section>
              <h2 className="text-lg font-bold text-slate-800 mb-3">Sediu social</h2>
              <p className="text-sm text-slate-700 flex items-start gap-2">
                <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-teal-600" />
                {OP.address_full}
              </p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-slate-800 mb-3">Contact</h2>
              <div className="space-y-2 text-sm">
                <a href={`mailto:${OP.email}`} className="flex items-center gap-2 text-slate-700 hover:text-teal-600">
                  <Mail className="w-4 h-4 text-teal-600" />
                  {OP.email}
                </a>
                <a href={`tel:${OP.phone.replace(/\s/g, "")}`} className="flex items-center gap-2 text-slate-700 hover:text-teal-600">
                  <Phone className="w-4 h-4 text-teal-600" />
                  {OP.phone}
                </a>
              </div>
            </section>

            <section className="border-t pt-6">
              <h2 className="text-lg font-bold text-slate-800 mb-3">Responsabil cu Protecția Datelor (DPO)</h2>
              <p className="text-sm text-slate-700">
                Pentru orice solicitare privind protecția datelor (GDPR — acces, rectificare, ștergere, portabilitate),
                contactați-ne la:{" "}
                <a href={`mailto:${OP.dpo_email}`} className="text-teal-600 hover:underline">{OP.dpo_email}</a>
              </p>
              <p className="text-xs text-slate-500 mt-2">
                Răspundem în maxim 30 de zile conform Art. 12 GDPR. Aveți de asemenea dreptul de a depune
                plângere la ANSPDCP — <a href={OP.anspdcp_url} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline">{OP.anspdcp_url}</a>
              </p>
            </section>

            <section className="border-t pt-6">
              <h2 className="text-lg font-bold text-slate-800 mb-3">Soluționarea litigiilor (ANPC SOL)</h2>
              <p className="text-sm text-slate-700">
                Conform Regulamentului UE 524/2013, dacă sunteți consumator nemulțumit, puteți utiliza
                <strong> Platforma de Soluționare Online a Litigiilor (SOL)</strong>:
              </p>
              <a
                href={OP.anpc_sol_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 mt-2 text-sm text-blue-600 hover:underline"
                data-testid="about-anpc-sol-link"
              >
                <ExternalLink className="w-4 h-4" />
                {OP.anpc_sol_url}
              </a>
            </section>

            <section className="border-t pt-6">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-5 h-5 text-teal-600" />
                <h2 className="text-lg font-bold text-slate-800">Cazuri de utilizare reale</h2>
              </div>
              <p className="text-sm text-slate-600 mb-4">
                Capturi din platformă — așa arată {OP.brand} pentru firmele de construcții și prestări servicii din România.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { src: "/screenshots/deviz.jpg", title: "Devize și DevizePRO AI", desc: "Bibliotecă de devize cu calcul automat TVA și sugestii de prețuri din proiectele tale anterioare." },
                  { src: "/screenshots/contract.jpg", title: "Contracte WYSIWYG cu anexe", desc: "Contracte de prestări servicii cu PV preluare, situații de lucrări și PV recepție — descărcabile în PDF." },
                  { src: "/screenshots/pontaj.jpg", title: "Calendar Pontaj digital", desc: "Pontaj zilnic cu validare GPS, sărbători legale, cereri de concediu și export PDF/Excel semnat." },
                ].map((s) => (
                  <button
                    key={s.src}
                    type="button"
                    onClick={() => setLightbox(s)}
                    className="group text-left bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-lg hover:border-teal-300 transition-all"
                    data-testid={`usecase-card-${s.title.split(" ")[0].toLowerCase()}`}
                  >
                    <div className="aspect-[16/10] overflow-hidden bg-slate-50">
                      <img
                        src={s.src}
                        alt={s.title}
                        loading="lazy"
                        className="w-full h-full object-cover object-top group-hover:scale-[1.03] transition-transform"
                      />
                    </div>
                    <div className="p-3">
                      <h3 className="text-sm font-semibold text-slate-800">{s.title}</h3>
                      <p className="text-xs text-slate-500 mt-1 leading-relaxed">{s.desc}</p>
                    </div>
                  </button>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-3 italic">
                * Capturile reflectă date demonstrative dintr-un cont de test. Toate firmele înregistrate au acces la aceleași funcționalități.
              </p>
            </section>

            <section className="border-t pt-6">
              <h2 className="text-lg font-bold text-slate-800 mb-3">Documente legale</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <a href="/legal?tab=privacy" className="flex items-center gap-2 p-3 rounded-lg border border-slate-200 hover:border-teal-300 hover:bg-teal-50 text-sm">
                  <Shield className="w-4 h-4 text-teal-600" />
                  Politică Confidențialitate
                </a>
                <a href="/legal?tab=terms" className="flex items-center gap-2 p-3 rounded-lg border border-slate-200 hover:border-teal-300 hover:bg-teal-50 text-sm">
                  <FileText className="w-4 h-4 text-teal-600" />
                  Termeni și Condiții
                </a>
                <a href="/legal?tab=cancellation" className="flex items-center gap-2 p-3 rounded-lg border border-slate-200 hover:border-teal-300 hover:bg-teal-50 text-sm">
                  <RotateCcw className="w-4 h-4 text-teal-600" />
                  Anulare / Retragere
                </a>
              </div>
            </section>
          </div>
        </div>
      </div>

      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          data-testid="usecase-lightbox"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
            className="absolute top-4 right-4 text-white/80 hover:text-white p-2 rounded-full bg-white/10 hover:bg-white/20 transition"
            data-testid="usecase-lightbox-close"
            aria-label="Închide"
          >
            <X className="w-5 h-5" />
          </button>
          <figure className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <img src={lightbox.src} alt={lightbox.title} className="w-full h-auto rounded-lg shadow-2xl" />
            <figcaption className="text-center text-white/90 text-sm mt-3">
              <strong>{lightbox.title}</strong> — {lightbox.desc}
            </figcaption>
          </figure>
        </div>
      )}

      <LegalFooter />
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] uppercase tracking-wide text-slate-500">{label}</span>
      <span className="font-semibold text-slate-800 break-words">{value}</span>
    </div>
  );
}
